import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit2, Trash2, Loader2, ExternalLink, Eye, EyeOff, Copy, Check } from 'lucide-react';
import { adminListMinisites, adminCreateMinisite, adminDeleteMinisite, adminUpdateMinisite, resolveImg } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

export default function AdminMinisites() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [copiedSlug, setCopiedSlug] = useState(null);
  const { toast } = useToast();

  const load = () => {
    setLoading(true);
    adminListMinisites().then(setItems).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const createNew = async () => {
    if (!newTitle.trim()) {
      toast({ title: 'Falta el título', variant: 'destructive' });
      return;
    }
    try {
      const created = await adminCreateMinisite({ title: newTitle.trim(), slug: newSlug.trim() });
      toast({ title: 'Minisite creado', description: `URL: /m/${created.slug}` });
      setCreating(false);
      setNewTitle('');
      setNewSlug('');
      load();
      // open editor
      window.location.href = `/admin/minisites/${created.id}`;
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo crear', variant: 'destructive' });
    }
  };

  const remove = async (item) => {
    if (!confirm(`¿Borrar el minisite "${i18nEs(item.title)}"? Esta acción no se puede deshacer.`)) return;
    await adminDeleteMinisite(item.id);
    load();
  };

  const toggleStatus = async (item) => {
    await adminUpdateMinisite(item.id, { status: item.status === 'published' ? 'draft' : 'published' });
    load();
  };

  const publicUrl = (slug) => `${window.location.origin}/m/${slug}`;

  const copyUrl = (slug) => {
    navigator.clipboard.writeText(publicUrl(slug));
    setCopiedSlug(slug);
    toast({ title: 'URL copiada', description: publicUrl(slug) });
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-minisites-page">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="font-serif-display text-3xl text-white mb-1">Minisites</h1>
          <p className="text-white/55 text-sm">Páginas independientes con URL personalizada (<code className="text-[#c9a063]">/m/&lt;slug&gt;</code>) para enlazar desde cualquier sitio del web o redes sociales.</p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="gd-btn-gold px-5 py-2.5 text-[11px] uppercase tracking-[0.3em] font-medium inline-flex items-center gap-2"
          data-testid="admin-minisites-new-btn"
        >
          <Plus size={14} /> Nuevo
        </button>
      </div>

      <div className="mt-6">
        {loading ? (
          <Loader2 className="animate-spin text-[#c9a063]" />
        ) : items.length === 0 ? (
          <div className="gd-card p-12 text-center text-white/40" data-testid="admin-minisites-empty">
            Aún no hay minisites. Pulsa "Nuevo" para crear el primero.
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <div key={item.id} className="gd-card p-4 flex gap-4 items-center" data-testid={`admin-minisite-row-${item.id}`}>
                <div className="w-24 h-16 bg-zinc-900 flex-shrink-0 overflow-hidden">
                  {item.header_image ? (
                    <img src={resolveImg(item.header_image)} alt={i18nEs(item.title)} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/20 text-xs">—</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#c9a063] mb-1">
                    <span>/m/{item.slug}</span>
                    <span className={`ml-2 px-2 py-0.5 border text-[9px] ${item.status === 'published' ? 'border-emerald-500/40 text-emerald-400' : 'border-amber-500/40 text-amber-400'}`}>
                      {item.status === 'published' ? 'Publicado' : 'Borrador'}
                    </span>
                  </div>
                  <h3 className="font-serif-display text-lg text-white truncate">{i18nEs(item.title) || '(sin título)'}</h3>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => copyUrl(item.slug)}
                    title="Copiar URL pública"
                    className="text-xs px-2 py-1.5 border border-white/15 text-white/60 hover:text-[#c9a063] hover:border-[#c9a063]"
                    data-testid={`admin-minisite-copy-${item.id}`}
                  >
                    {copiedSlug === item.slug ? <Check size={12} /> : <Copy size={12} />}
                  </button>
                  <a
                    href={`/m/${item.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    title="Ver minisite público"
                    className="text-xs px-2 py-1.5 border border-white/15 text-white/60 hover:text-[#c9a063] hover:border-[#c9a063]"
                    data-testid={`admin-minisite-view-${item.id}`}
                  >
                    <ExternalLink size={12} />
                  </a>
                  <button
                    onClick={() => toggleStatus(item)}
                    title={item.status === 'published' ? 'Pasar a borrador' : 'Publicar'}
                    className="text-xs px-2 py-1.5 border border-white/15 text-white/60 hover:text-[#c9a063] hover:border-[#c9a063]"
                    data-testid={`admin-minisite-toggle-${item.id}`}
                  >
                    {item.status === 'published' ? <Eye size={12} /> : <EyeOff size={12} />}
                  </button>
                  <Link
                    to={`/admin/minisites/${item.id}`}
                    title="Editar"
                    className="text-xs px-2 py-1.5 border border-white/15 text-white/60 hover:text-[#c9a063] hover:border-[#c9a063]"
                    data-testid={`admin-minisite-edit-${item.id}`}
                  >
                    <Edit2 size={12} />
                  </Link>
                  <button
                    onClick={() => remove(item)}
                    title="Borrar"
                    className="text-xs px-2 py-1.5 border border-white/15 text-white/60 hover:text-red-400 hover:border-red-400"
                    data-testid={`admin-minisite-delete-${item.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create modal */}
      {creating && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="admin-minisite-create-modal">
          <div className="bg-[#0d0d0e] border border-white/10 w-full max-w-md p-6">
            <h2 className="font-serif-display text-2xl text-white mb-5">Nuevo minisite</h2>
            <div className="space-y-4">
              <div>
                <label className="text-[10px] tracking-widest text-white/50 uppercase block mb-1.5">Título *</label>
                <input
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  className="bg-zinc-950 border border-white/10 text-white/90 px-3 py-2 text-sm focus:border-[#c9a063] focus:outline-none w-full"
                  placeholder="Ej: Open de Golf Divino Sevilla 2026"
                  data-testid="admin-minisite-create-title"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-widest text-white/50 uppercase block mb-1.5">Slug (URL) — opcional</label>
                <input
                  value={newSlug}
                  onChange={(e) => setNewSlug(e.target.value)}
                  className="bg-zinc-950 border border-white/10 text-white/90 px-3 py-2 text-sm focus:border-[#c9a063] focus:outline-none w-full"
                  placeholder="open-sevilla-2026"
                  data-testid="admin-minisite-create-slug"
                />
                <p className="text-[10px] text-white/40 mt-1">Si lo dejas vacío, se genera del título. La URL final será: <span className="text-[#c9a063]">/m/{(newSlug || newTitle || 'tu-slug').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}</span></p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => { setCreating(false); setNewTitle(''); setNewSlug(''); }} className="px-5 py-2.5 text-xs uppercase tracking-widest text-white/70" data-testid="admin-minisite-create-cancel">Cancelar</button>
              <button onClick={createNew} className="gd-btn-gold px-6 py-2.5 text-[11px] uppercase tracking-[0.3em] font-medium" data-testid="admin-minisite-create-confirm">Crear y editar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
